import type { ZnHeaderProps } from '~/components/Zenni/ZnHeader/types'

export interface CategoryMetadata {
  key: string
  title: string
  mobileTitle?: string
  breadcrumbTitle?: string
  href: string
  initialFilter: Record<string, string[]>
  header?: ZnHeaderProps
}

export const KIDS_GENDER_GROUP_STRING =
  '[girl_s,boy_s,unisex_kids,Girl_s,Boy_s,Unisex_Kids]'
export const WOMEN_GENDER_GROUP_STRING = '[women_s,Women_s,Unisex,unisex]'
export const MEN_GENDER_GROUP_STRING = '[men_s,Men_s,Unisex,unisex]'
export const SPORTS_GROUP_STRING = '[ZunniesSports,nonrxsunglasses]'
export const CATEGORIES_METADATA = (
  hasDiscount = false,
): CategoryMetadata[] => [
  {
    key: 'women',
    title: "Women's Glasses",
    href: '/products/list/women',
    initialFilter: {
      Frame___Gender_search: [WOMEN_GENDER_GROUP_STRING],
    },
  },
  {
    key: 'men',
    title: "Men's Glasses",
    href: '/products/list/men',
    initialFilter: {
      Frame___Gender_search: [MEN_GENDER_GROUP_STRING],
    },
  },
  {
    key: 'unisex',
    title: 'Unisex Glasses',
    href: '/products/list/unisex',
    initialFilter: {
      Frame___Gender_search: ['unisex'],
    },
  },
  {
    key: 'nonRx',
    title: 'Non-Prescription Glasses',
    href: '/products/list/non-rx',
    initialFilter: {
      Frame___Prescription_types_search: ['nonprescription'],
    },
  },
  {
    key: 'prescription',
    title: 'Prescription Glasses',
    href: '/products/list/prescription',
    initialFilter: {
      Frame___Prescription_types_search: ['prescription'],
    },
  },
  {
    key: 'kids',
    title: "Kids' Eyeglasses",
    href: '/products/list/kids',
    initialFilter: {
      Frame___Gender_search: [KIDS_GENDER_GROUP_STRING],
    },
  },
  {
    key: 'sunglasses',
    title: 'Sunglasses',
    href: '/products/list/sunglasses',
    initialFilter: {
      Frame___Gender_search: [
        WOMEN_GENDER_GROUP_STRING,
        MEN_GENDER_GROUP_STRING,
      ],
    },
  },
  {
    key: 'womenSunglasses',
    title: "Women's Sunglasses",
    href: '/products/list/women-sunglasses',
    initialFilter: {
      Frame___Gender_search: [WOMEN_GENDER_GROUP_STRING],
    },
  },
  {
    key: 'menSunglasses',
    title: "Men's Sunglasses",
    href: '/products/list/men-sunglasses',
    initialFilter: {
      Frame___Gender_search: [MEN_GENDER_GROUP_STRING],
    },
  },
  {
    key: 'kidsSunglasses',
    title: "Kids' Sunglasses",
    href: '/products/list/kids-sunglasses',
    initialFilter: {
      Frame___Gender_search: [KIDS_GENDER_GROUP_STRING],
    },
  },
  {
    key: 'bestselling',
    title: 'Bestselling Eyeglasses',
    href: '/products/list/bestselling',
    initialFilter: {
      Frame___Gender_search: [
        WOMEN_GENDER_GROUP_STRING,
        MEN_GENDER_GROUP_STRING,
      ],
    },
  },
  {
    key: 'highPrescription',
    title: 'High Prescription',
    href: '/products/list/highprescription',
    initialFilter: {
      Frame___Features_search: ['supportshighprescriptions'],
      Frame___Gender_search: [
        WOMEN_GENDER_GROUP_STRING,
        MEN_GENDER_GROUP_STRING,
      ],
    },
  },
  {
    key: 'bestsellingSunglasses',
    title: 'Bestselling Sunglasses',
    href: '/products/list/bestselling-sunglasses',
    initialFilter: {
      Frame___Gender_search: [
        WOMEN_GENDER_GROUP_STRING,
        MEN_GENDER_GROUP_STRING,
      ],
    },
  },
  {
    key: 'progressive',
    title: 'Progressives',
    href: '/products/list/progressive',
    initialFilter: {
      Frame___Prescription_types_search: ['progressive'],
    },
  },
  {
    key: 'sports',
    title: 'Zunnies™ Prescription Ready Sports Sunglasses',
    mobileTitle: 'Zunnies™ Sports Sunglasses',
    breadcrumbTitle: 'Sports',
    href: '/products/list/sports',
    initialFilter: {
      frame_type_code_search: [SPORTS_GROUP_STRING],
    },
    header: {
      title: hasDiscount
        ? 'SPORTY SUNNIES,<br>EXCLUSIVE SAVINGS'
        : 'SPORTY SUNNIES, <br>Rx-READY',
      subtitle:
        'Add your prescription and move — features that let you groove.',
      backgroundImage: '/img/plp/banner-sports.jpg',
      mobileBackgroundImage: '/img/plp/banner-sports-sm.jpg',
      textColor: '#333333',
    },
  },
  {
    key: 'sportsNonRx',
    title: 'Zunnies™ Sports Sunglasses',
    breadcrumbTitle: 'Sports',
    href: '/products/list/sports-non-rx',
    initialFilter: {
      frame_type_code_search: [SPORTS_GROUP_STRING],
      Frame___Prescription_types_search: ['nonprescription'],
    },
    header: {
      title: 'SPORTY SUNNIES',
      subtitle: 'Features that let you groove.',
      backgroundImage: '/img/plp/banner-sports.jpg',
      mobileBackgroundImage: '/img/plp/banner-sports-sm.jpg',
      textColor: '#333333',
    },
  },
  {
    key: 'sportsRxOnly',
    title: 'Zunnies™ Prescription Ready Sports Sunglasses',
    breadcrumbTitle: 'Sports',
    href: '/products/list/sports-rx-only',
    initialFilter: {
      frame_type_code_search: [SPORTS_GROUP_STRING],
      Frame___Prescription_types_search: ['prescription'],
    },
    header: {
      title: 'SPORTY SUNNIES,<br>Rx-READY',
      subtitle:
        'Add your prescription and move—<br>features that let you groove.',
      backgroundImage: '/img/plp/banner-sports.jpg',
      mobileBackgroundImage: '/img/plp/banner-sports-sm.jpg',
      textColor: '#333333',
    },
  },
  {
    key: 'premium',
    title: 'The Premium Eyewear Collection',
    breadcrumbTitle: 'Premium',
    href: '/products/list/premium',
    initialFilter: {
      frame_type_code_search: ['PremiumEyeglasses', 'premiumeyeglasses'],
    },
    header: {
      title: 'Our Most Refined<br>Frames',
      subtitle: 'Innovative styles in sleek acetate and<br>stainless steel.',
      backgroundImage: '/img/plp/banner-premium.jpg',
      mobileBackgroundImage: '/img/plp/banner-premium-sm.jpg',
      textColor: '#333333',
    },
  },
  {
    key: 'optiflex',
    title: 'OptiFlex Frames',
    breadcrumbTitle: 'OptiFlex',
    href: '/products/list/optiflex',
    initialFilter: {
      frame_type_code_search: ['Optiflex', 'optiflex'],
    },
    header: {
      title: 'Ultra-Flexible and<br>Lightweight',
      subtitle: 'Made with comfortable,<br>hypoallergenic ULTEM material.',
      backgroundImage: '/img/plp/banner-optiflex.jpg',
      mobileBackgroundImage: '/img/plp/banner-optiflex-sm.jpg',
      textColor: '#FFFFFF',
    },
  },
  {
    key: 'freeWithCredit',
    title: 'Free with Credit',
    href: '/products/list/free-with-credit',
    initialFilter: {
      Is_Under_150_Limit: ['true'],
    },
  },
]
